// PopularPlaces - Top rated places

import React, { useState, useEffect } from 'react';
import { TrendingUp, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import type { Place } from '../../types';
import { getPopularPlaces } from '../../services/placesService';
import { PlaceCard } from '../places/PlaceCard';

interface PopularPlacesProps {
  favorites: string[];
  onSelectPlace: (place: Place) => void;
  onToggleFavorite: (placeId: string) => void;
}

export function PopularPlaces({
  favorites,
  onSelectPlace,
  onToggleFavorite,
}: PopularPlacesProps) {
  const [places, setPlaces] = useState<Place[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [scrollIndex, setScrollIndex] = useState(0);

  useEffect(() => {
    async function loadPopular() {
      setIsLoading(true);
      try {
        const popular = await getPopularPlaces(8);
        setPlaces(popular);
      } catch (error) {
        console.error('Error loading popular places:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadPopular();
  }, []);

  const canScrollLeft = scrollIndex > 0;
  const canScrollRight = scrollIndex < Math.max(0, places.length - 4);

  const scrollLeft = () => {
    setScrollIndex((prev) => Math.max(0, prev - 1));
  };

  const scrollRight = () => {
    setScrollIndex((prev) => Math.min(places.length - 4, prev + 1));
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="aspect-[20/19] bg-gray-200 rounded-xl mb-3" />
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (places.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-xl">
        <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">
          Noch keine bewerteten Orte vorhanden.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Navigation Buttons */}
      {canScrollLeft && (
        <button
          onClick={scrollLeft}
          className="absolute left-0 top-1/3 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={scrollRight}
          className="absolute right-0 top-1/3 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {/* Places Grid */}
      <div className="overflow-hidden">
        <div
          className="flex gap-4 transition-transform duration-300"
          style={{ transform: `translateX(-${scrollIndex * 25}%)` }}
        >
          {places.map((place, index) => (
            <div key={place.id} className="w-full sm:w-1/2 md:w-1/4 flex-shrink-0 relative">
              {/* Ranking Badge */}
              {index < 3 && (
                <div className="absolute -top-2 -left-2 z-10 w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-lg">
                  {index + 1}
                </div>
              )}
              <PlaceCard
                place={place}
                isFavorite={favorites.includes(place.id)}
                onSelect={onSelectPlace}
                onToggleFavorite={onToggleFavorite}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-400">
        <span className="flex items-center gap-1">
          <Star className="w-3 h-3" />
          Sortiert nach Bewertung
        </span>
      </div>
    </div>
  );
}

export default PopularPlaces;
